import { css, cx } from '@repo/pandacss/styled-system/css';
import Image from 'next/image';
import { CSSProperties, PropsWithChildren, Ref } from 'react';

const FLAG_TAIL_WIDTH_PX = 20;
export const BANNER_HEIGHT_FIXED_PX = 32;

type Props = PropsWithChildren<{
  variant?: 'row' | 'card' | 'row-compact';
  ref?: Ref<HTMLElement>;
  className?: string;
}>;

export function BestOfAwardsRoundupBanner({ children, ref, className, variant = 'row' }: Props) {
  if (variant === 'card') {
    return (
      <CardVariant ref={ref} className={className}>
        {children}
      </CardVariant>
    );
  }

  return (
    <div
      ref={ref as Ref<HTMLDivElement>}
      className={cx(
        css({
          containerType: 'inline-size',
          display: 'flex',
          flexDirection: 'row',
          width: 'full',
        }),
        className,
      )}
    >
      <div
        className={css({
          backgroundColor: 'green.light',
          display: 'inline-flex',
          height: 'var(--banner-height)',
          paddingLeft: '4',
        })}
        style={
          {
            '--banner-height': variant === 'row-compact' ? `${BANNER_HEIGHT_FIXED_PX}px` : 'auto',
          } as CSSProperties
        }
      >
        <Image
          width={32}
          height={40}
          src="https://www.nerdwallet.com/cdn/banking/icons/best_of_awards_2026_badge.svg"
          alt="NerdWallet Best-Of Awards"
          unoptimized
          style={{ objectFit: 'cover' }}
          className={css({
            height: variant === 'row-compact' ? { base: '[32px]', sm: '[40px]' } : '[40px]',
            width: variant === 'row-compact' ? { base: '[26px]', sm: '[32px]' } : '[32px]',
          })}
        />
      </div>
      <div
        className={cx(
          css({
            backgroundColor: 'green.light',
            display: 'flex',
            flexDirection: 'row',
            columnGap: '4',
            height: 'var(--banner-height)',
            paddingLeft: '4',
            paddingBlockStart: '1',
            paddingBlockEnd: '1',
            clipPath: 'var(--flag-tail-clip-path)',
            paddingRight: 'var(--flag-tail-pr)',
            width: variant === 'row-compact' ? { base: '[100%]', sm: 'auto' } : 'auto',
          }),
          variant === 'row' && css({ flexShrink: '0' }),
        )}
        style={
          {
            '--banner-height': variant === 'row' ? `${BANNER_HEIGHT_FIXED_PX}px` : 'auto',
            '--flag-tail-pr': `${FLAG_TAIL_WIDTH_PX + 8}px`,
            '--flag-tail-clip-path': `polygon(0 0, 100% 0, calc(100% - ${FLAG_TAIL_WIDTH_PX}px / 2) 50%, 100% 100%, 0 100%)`,
          } as CSSProperties
        }
      >
        <span
          className={css({
            fontWeight: 'bold',
            color: 'green.dark',
            display: 'inline-flex',
            alignItems: 'center',
            textTransform: 'uppercase',
            textStyle: 'textExtraSmall',
          })}
        >
          {children}
        </span>
      </div>
    </div>
  );
}

function CardVariant({ children, ref, className }: Omit<Props, 'variant'>) {
  return (
    <div
      ref={ref as Ref<HTMLDivElement>}
      className={cx(
        css({
          containerType: 'inline-size',
          display: 'flex',
          flexDirection: 'row',
          width: 'full',
          alignItems: 'start',
        }),
        className,
      )}
    >
      <div
        className={css({
          display: 'inline-flex',
        })}
      >
        <Image
          width={45}
          height={56}
          src="https://www.nerdwallet.com/cdn/banking/icons/best_of_awards_2026_badge.svg"
          alt="NerdWallet Best-Of Awards"
          unoptimized
          style={{ objectFit: 'cover' }}
        />
      </div>
      <div
        className={css({
          backgroundColor: 'green.light',
          display: 'flex',
          flexDirection: 'row',
          borderBottomRightRadius: '2',
          borderTopRightRadius: '2',
          minHeight: '10',
          paddingInlineStart: '4',
          paddingInlineEnd: '4',
          paddingBlockStart: '1',
          paddingBlockEnd: '1',
          '@container (max-width: 768px)': {
            width: 'full',
          },
        })}
      >
        <span
          className={css({
            textStyle: 'accent',
            color: 'green.dark',
            display: 'inline-flex',
            alignItems: 'center',
            lineHeight: '[150%]',
          })}
        >
          {children}
        </span>
      </div>
    </div>
  );
}
